import React, { ElementType } from 'react';
import styled from 'styled-components';
import { colors, breakpoints, typography, spaces } from '../../settings';

const headingLevelComponent: Record<typography.HeadingLevel, ElementType> = {
    1: 'h1',
    2: 'h2',
    3: 'h3',
    4: 'h4',
    5: 'h5',
    6: 'h6',
    7: 'span',
};

export interface StandardHeadingProps extends React.HTMLAttributes<HTMLElement> {
    component?: ElementType<React.HTMLAttributes<HTMLElement>>;
    accessibilityLevel?: typography.HeadingLevel;
    children: React.ReactNode;
    color?: colors.Color;
    fontFamily?: typography.FontFamily;
    fontWeight?: string;
    withDivider?: boolean;
    LEGACY_marginBottom?: spaces.Space;
}

export interface HeadingProps extends StandardHeadingProps {
    appearanceLevel?: typography.HeadingLevel;
    accessibilityLevel: typography.HeadingLevel;
}

export const Heading = styled((props: HeadingProps) => {
    const {
        component,
        accessibilityLevel,
        appearanceLevel: appearanceLevelIgnored,
        fontFamily: fontFamilyIgnored,
        fontWeight: fontWeightIgnored,
        color: colorIgnored,
        withDivider: withDividerIgnored,
        LEGACY_marginBottom: LEGACY_marginBottomIgnored,
        ...headingProps
    } = props;

    const Component = component || headingLevelComponent[accessibilityLevel];
    return <Component {...headingProps} />;
})<HeadingProps>`
    ${({ appearanceLevel, accessibilityLevel }) => {
        const headingLevel = appearanceLevel || accessibilityLevel;
        return `
            font-size: ${typography.headings[headingLevel].fontSize[1]}px;
            @media (min-width: 0) and (max-width: ${breakpoints.medium[1]}px) {
                font-size: ${typography.headings[headingLevel].fontSize[0]}px;
            }
            margin-top: 0;
            margin-bottom: 0;
            line-height: ${typography.headings[headingLevel].lineHeight};
            letter-spacing: ${typography.headings[headingLevel].letterSpacing};
    `;
    }}
    ${({ fontFamily = 'heading' }) => `font-family: ${typography.fontFamilies[fontFamily]};`}
    ${({ fontWeight = 400 }) => `font-weight: ${fontWeight};`}
    ${({ color }) => color && `color: ${colors[color]};`}
    ${({ withDivider }) =>
        withDivider &&
        `
        &:after {
            content: '—';
            display: block;
        }
    `}
    ${({ LEGACY_marginBottom }) =>
        LEGACY_marginBottom && `margin-bottom: ${spaces.sizes[LEGACY_marginBottom]};`}
`;

export function PageHeading({ accessibilityLevel = 1, ...props }: StandardHeadingProps) {
    return <Heading accessibilityLevel={accessibilityLevel} appearanceLevel={3} {...props} />;
}

export function JumboPageHeading({ accessibilityLevel = 1, ...props }: StandardHeadingProps) {
    return <Heading accessibilityLevel={accessibilityLevel} appearanceLevel={2} {...props} />;
}

export function SmallPageHeading({ accessibilityLevel = 1, ...props }: StandardHeadingProps) {
    return <Heading accessibilityLevel={accessibilityLevel} appearanceLevel={4} {...props} />;
}

export function PageSubHeading({ accessibilityLevel = 2, ...props }: StandardHeadingProps) {
    return (
        <Heading
            accessibilityLevel={accessibilityLevel}
            appearanceLevel={6}
            color="fiftyFirstShade"
            fontFamily="text"
            {...props}
        />
    );
}

export function SectionHeading({ accessibilityLevel = 2, ...props }: StandardHeadingProps) {
    return <Heading accessibilityLevel={accessibilityLevel} appearanceLevel={4} {...props} />;
}

export function SmallSectionHeading({ accessibilityLevel = 3, ...props }: StandardHeadingProps) {
    return (
        <Heading
            accessibilityLevel={accessibilityLevel}
            appearanceLevel={6}
            fontFamily="text"
            fontWeight="bold"
            {...props}
        />
    );
}

export function SmallSectionSubheading({ accessibilityLevel = 4, ...props }: StandardHeadingProps) {
    return (
        <Heading
            accessibilityLevel={accessibilityLevel}
            appearanceLevel={5}
            fontFamily="text"
            {...props}
        />
    );
}

export function FormHeading({ accessibilityLevel = 4, ...props }: StandardHeadingProps) {
    return (
        <Heading
            accessibilityLevel={accessibilityLevel}
            appearanceLevel={5}
            fontFamily="text"
            {...props}
        />
    );
}

export function OverlayHeading({ accessibilityLevel = 4, ...props }: StandardHeadingProps) {
    return (
        <Heading
            accessibilityLevel={accessibilityLevel}
            appearanceLevel={4}
            fontFamily="text"
            {...props}
        />
    );
}
